import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { getGeneralSettings } from '@/lib/settingsStore';
import { useTenant } from '@/contexts/TenantContext';

interface GlobalLoadingContextType {
  isLoading: boolean;
  withLoading: <T>(fn: () => Promise<T>) => Promise<T>;
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType>({
  isLoading: false,
  withLoading: async (fn) => fn(),
});

export const useGlobalLoading = () => useContext(GlobalLoadingContext);

const GlobalLoadingOverlay = () => {
  const settings = getGeneralSettings();
  const { company } = useTenant();
  const logo = settings.sidebarLogo || settings.companyLogo || company?.sidebar_logo_url || company?.logo_url;
  const radius = 45;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-4 animate-scale-in">
        <div className="relative w-48 h-48">
          <svg className="absolute inset-0 w-48 h-48 -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50" cy="50" r={radius}
              fill="none"
              stroke="hsl(var(--muted))"
              strokeWidth="4"
              opacity="0.3"
            />
            <circle
              cx="50" cy="50" r={radius}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="4.5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * 0.25}
              className="animate-spin"
              style={{
                transformOrigin: '50px 50px',
                animationDuration: '1.5s',
                filter: 'drop-shadow(0 0 6px hsl(var(--primary) / 0.5))',
              }}
            />
            <circle
              cx="50" cy="50" r={radius}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="20 60"
              opacity="0.4"
              className="animate-spin"
              style={{ transformOrigin: '50px 50px', animationDuration: '2.5s', animationDirection: 'reverse' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {logo ? (
              <img src={logo} alt="Logo" className="w-28 h-28 object-contain rounded-full dark:brightness-0 dark:invert" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-3xl font-bold text-primary">M</span>
              </div>
            )}
          </div>
        </div>
        <p className="text-white/80 text-sm font-medium animate-pulse">جاري التحميل...</p>
      </div>
    </div>
  );
};

export const GlobalLoadingProvider = ({ children }: { children: ReactNode }) => {
  const [count, setCount] = useState(0);

  const withLoading = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setCount(c => c + 1);
    try {
      return await fn();
    } finally {
      setCount(c => c - 1);
    }
  }, []);

  return (
    <GlobalLoadingContext.Provider value={{ isLoading: count > 0, withLoading }}>
      {children}
      {count > 0 && <GlobalLoadingOverlay />}
    </GlobalLoadingContext.Provider>
  );
};
